/* eslint-disable max-lines */
import { useCameraGroups, useCameras } from '@hakimo-ui/hakimo/data-access';
import { ListView } from '@hakimo-ui/hakimo/feature-shared';
import {
  Camera,
  CameraGroup,
  LocationFilterValue,
} from '@hakimo-ui/hakimo/types';
import { CameraGroupIcon } from '@hakimo-ui/hakimo/ui-elements';
import { LocationFilter } from '@hakimo-ui/hakimo/ui-filters';
import { VideoCameraIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import {
  trackFilters,
  useCanViewCameraGroup,
  useDebounce,
} from '@hakimo-ui/hakimo/util';
import { Alert, HakimoSpinner, InputField } from '@hakimo-ui/shared/ui-base';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getCameraGroupSearchParams } from '../../camera-groups/util';
import { getSearchParams } from '../../cameras-list/util';
import DraggableRow from './DraggableRow';
import MultiCamCameraGroupRow from './MultiCamCameraGroupRow';
import MultiCamCameraRow from './MultiCamCameraRow';
import {
  activeTabAtom,
  committedCameraMapAtom,
  committedGroupMapAtom,
  multiCamFilterAtom,
} from '../state';
import useMultiCamSelection from '../useMultiCamSelection';
import { PAGE_SIZE } from '../util';

type SelectionView = 'list' | 'groups';

export function MultiCamSelectionPanel() {
  const canViewCameraGroup = useCanViewCameraGroup();
  const [view, setView] = useState<SelectionView>('list');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [camFilters, setCamFilters] = useAtom(multiCamFilterAtom);
  const hasLocation = camFilters.location.values.length > 0;

  const committedCameraMap = useAtomValue(committedCameraMapAtom);
  const committedGroupMap = useAtomValue(committedGroupMapAtom);
  const activeTab = useAtomValue(activeTabAtom);
  const { commit, removeCameras, removeGroup } = useMultiCamSelection();

  // Anchor index for shift-click range selection in the current list.
  const anchorRef = useRef<number | null>(null);
  // Tracks the tab we last synced the list section from, so switching header
  // tabs flips the list to the matching section without fighting a later
  // manual toggle.
  const prevActiveTabRef = useRef<string | null>(null);

  const [preview, setPreview] = useState<{
    camera: Camera;
    top: number;
    left: number;
  } | null>(null);

  const onHoverItem = (camera: Camera, e: React.MouseEvent<HTMLDivElement>) => {
    if (!camera.imageUrl) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setPreview({ camera, top: rect.top, left: rect.right + 8 });
  };

  const cameraSearchParams = useMemo(
    () =>
      getSearchParams(camFilters, {
        searchedCameraName: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
        includeImage: true,
      }),
    [camFilters, debouncedSearch, page]
  );
  const groupSearchParams = useMemo(
    () =>
      getCameraGroupSearchParams(
        page,
        PAGE_SIZE,
        debouncedSearch,
        camFilters.location.values.map((loc) => loc.id)
      ),
    [page, debouncedSearch, camFilters.location.values]
  );

  const { data, isFetching, isError, error } = useCameras(
    cameraSearchParams,
    hasLocation && view === 'list'
  );
  const {
    data: groupData,
    isFetching: isFetchingGroups,
    isError: isGroupError,
    error: groupError,
  } = useCameraGroups(
    groupSearchParams,
    hasLocation && view === 'groups' && canViewCameraGroup
  );

  const selectedCount =
    view === 'list'
      ? Object.keys(committedCameraMap).length
      : Object.keys(committedGroupMap).length;

  const hasSelectionOnPage = (data?.items ?? []).some(
    (cam) => committedCameraMap[cam.id]
  );

  const onChangeView = (nextView: SelectionView) => {
    if (nextView === view) return;
    if (nextView === 'groups' && !canViewCameraGroup) return;
    anchorRef.current = null;
    setPage(1);
    setView(nextView);
  };

  useEffect(() => {
    const activeTabId = activeTab?.id ?? null;
    if (activeTabId === prevActiveTabRef.current) return;
    prevActiveTabRef.current = activeTabId;
    if (!activeTab) return;
    const nextView: SelectionView =
      activeTab.type === 'group' ? 'groups' : 'list';
    if (nextView === view) return;
    if (nextView === 'groups' && !canViewCameraGroup) return;
    anchorRef.current = null;
    setPage(1);
    setView(nextView);
  }, [activeTab, view, canViewCameraGroup]);

  const onChangePage = (nextPage: number) => {
    anchorRef.current = null;
    setPage(nextPage);
  };

  const onSearchValChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    anchorRef.current = null;
    setPage(1);
  };

  const onChangeLocation = (location: LocationFilterValue) => {
    const next = { ...camFilters, location };
    setCamFilters(next);
    trackFilters(next);
    anchorRef.current = null;
    setPage(1);
  };

  // Shared shift-range + single-toggle logic for both lists; callers supply
  // the page items, whether the row is already selected, and the add/remove.
  const toggleSelection = <T,>(
    items: T[],
    index: number,
    e: React.MouseEvent,
    isChecked: boolean,
    onCommit: (range: T[]) => void,
    onRemove: () => void
  ) => {
    if (e.shiftKey && anchorRef.current !== null) {
      const lo = Math.min(anchorRef.current, index);
      const hi = Math.max(anchorRef.current, index);
      onCommit(items.slice(lo, hi + 1));
      return;
    }
    anchorRef.current = index;
    if (isChecked) onRemove();
    else onCommit([items[index]]);
  };

  const toggleCamera = (camera: Camera, index: number, e: React.MouseEvent) =>
    toggleSelection(
      data?.items || [],
      index,
      e,
      !!committedCameraMap[camera.id],
      (range) => commit(range, []),
      () => removeCameras([camera.id])
    );

  const toggleGroup = (
    group: CameraGroup,
    index: number,
    e: React.MouseEvent
  ) =>
    toggleSelection(
      groupData?.items || [],
      index,
      e,
      !!committedGroupMap[group.cameraGroupId],
      (range) => commit([], range),
      () => removeGroup(group.cameraGroupId)
    );

  const onSelectAll = () => {
    commit(data?.items || [], []);
  };

  const onDeselectAll = () => {
    removeCameras((data?.items || []).map((cam) => cam.id));
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-shrink-0 flex-col gap-3 px-4 pt-1">
        <div>
          <label className="dark:text-ondark-text-1 text-onlight-text-1 mb-1 block text-sm font-medium">
            Select Location
            <span className="text-red-500"> *</span>
          </label>
          <LocationFilter
            value={camFilters.location}
            label=""
            onChange={onChangeLocation}
            selectedMaxHeightClassName="max-h-20"
          />
        </div>
        {hasLocation && (
          <>
            <div className="flex">
              <button
                type="button"
                onClick={() => onChangeView('list')}
                className={clsx(
                  'flex flex-1 items-center justify-center gap-2 rounded-l-md border px-3 py-2 text-sm font-medium transition-colors',
                  !canViewCameraGroup && 'rounded-r-md',
                  view === 'list'
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-onlight-line-2 dark:border-ondark-line-2 text-onlight-text-2 dark:text-ondark-text-2 hover:bg-onlight-bg-3 dark:hover:bg-ondark-bg-3'
                )}
              >
                <VideoCameraIcon className="h-4 w-4 flex-none" />
                Cameras List
              </button>
              {canViewCameraGroup && (
                <button
                  type="button"
                  onClick={() => onChangeView('groups')}
                  className={clsx(
                    '-ml-px flex flex-1 items-center justify-center gap-2 rounded-r-md border px-3 py-2 text-sm font-medium transition-colors',
                    view === 'groups'
                      ? 'border-primary-600 bg-primary-600 text-white'
                      : 'border-onlight-line-2 dark:border-ondark-line-2 text-onlight-text-2 dark:text-ondark-text-2 hover:bg-onlight-bg-3 dark:hover:bg-ondark-bg-3'
                  )}
                >
                  <CameraGroupIcon className="h-4 w-4 flex-none" />
                  Camera Groups
                </button>
              )}
            </div>
            <InputField
              label=""
              onChange={onSearchValChange}
              value={search}
              type="search"
              placeholder={
                view === 'list'
                  ? 'Search cameras...'
                  : 'Search camera groups...'
              }
            />
          </>
        )}
      </div>

      {!hasLocation && (
        <div className="text-onlight-text-2 dark:text-ondark-text-2 flex flex-1 items-center justify-center px-6 text-center text-sm">
          Select a location to see cameras and camera groups.
        </div>
      )}

      {hasLocation && (
        <>
          <div className="flex flex-none items-center gap-4 px-5 pb-2 pt-4 text-sm">
            {view === 'list' && (
              <button
                type="button"
                role="checkbox"
                aria-checked={hasSelectionOnPage}
                title={hasSelectionOnPage ? 'Deselect all' : 'Select all'}
                onClick={() =>
                  hasSelectionOnPage ? onDeselectAll() : onSelectAll()
                }
                className={clsx(
                  'flex h-5 w-5 flex-none items-center justify-center rounded-md border-2 outline-none transition-colors',
                  hasSelectionOnPage
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-onlight-line-3 dark:border-ondark-line-2'
                )}
              >
                {hasSelectionOnPage && <CheckIcon className="h-3.5 w-3.5" />}
              </button>
            )}
            <span className="text-onlight-text-2 dark:text-ondark-text-2 ml-auto flex items-center gap-2">
              {(view === 'list' ? isFetching : isFetchingGroups) && (
                <HakimoSpinner />
              )}
              {selectedCount} Selected
            </span>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {view === 'list' && isError && (
              <Alert type="error">{error?.message}</Alert>
            )}
            {view === 'groups' && canViewCameraGroup && isGroupError && (
              <Alert type="error">{groupError?.message}</Alert>
            )}
            {view === 'list' && !isError && (
              <ListView
                items={data?.items || []}
                total={data?.total || 0}
                page={page}
                pageSize={PAGE_SIZE}
                onChangePage={onChangePage}
                renderListItem={(camera, index) => {
                  const checked = !!committedCameraMap[camera.id];
                  return (
                    <div
                      onMouseEnter={(e) => onHoverItem(camera, e)}
                      onMouseLeave={() => setPreview(null)}
                    >
                      <DraggableRow
                        id={camera.id}
                        data={{ type: 'camera', camera, checked }}
                      >
                        <MultiCamCameraRow
                          camera={camera}
                          checked={checked}
                          onToggle={(e) => toggleCamera(camera, index, e)}
                        />
                      </DraggableRow>
                    </div>
                  );
                }}
              />
            )}
            {view === 'groups' && canViewCameraGroup && !isGroupError && (
              <ListView
                items={(groupData?.items || []).map((group) => ({
                  ...group,
                  id: group.cameraGroupId,
                }))}
                total={groupData?.total || 0}
                page={page}
                pageSize={PAGE_SIZE}
                onChangePage={onChangePage}
                renderListItem={(group, index) => {
                  const checked = !!committedGroupMap[group.cameraGroupId];
                  return (
                    <DraggableRow
                      id={group.cameraGroupId}
                      data={{ type: 'group', group, checked }}
                    >
                      <MultiCamCameraGroupRow
                        group={group}
                        checked={checked}
                        onToggle={(e) => toggleGroup(group, index, e)}
                      />
                    </DraggableRow>
                  );
                }}
              />
            )}
          </div>
        </>
      )}

      {preview && (
        <img
          key={preview.camera.id}
          src={preview.camera.imageUrl}
          alt={preview.camera.name}
          style={{ top: preview.top, left: preview.left }}
          className="pointer-events-none fixed z-50 w-64 rounded object-contain shadow-lg"
        />
      )}
    </div>
  );
}

export default MultiCamSelectionPanel;
